import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import type { RootState, AppDispatch } from '../../store'
import { useWeb3Context } from '../../contexts/web3Context'
import { getNotificationDisplayText } from '../../common/state/notifications/displayText'
import { markNotificationRead } from '../../common/state/notifications/action'
import type { Notification } from '../../common/state/notifications/state'
import { isOnBattlePath } from '../../common/battleRoutes'

/**
 * Top-centre global notification ticker (all pages except battle). Shows only NEW notifications that
 * arrive after mount (not the existing backlog) as daily-request-style ribbons — max 5 per stack,
 * newest nearest the top edge; when a newer one arrives at the cap the oldest slides out. Each line
 * fades in when it appears and out when it leaves, auto-disappears (10s routine, longer for war),
 * marquees long text every 30s, exposes a close button on hover, and opens the notification modal on
 * click. While the MvM attack banner owns the top strip the whole ticker drops below it.
 */

const MAX_TOASTS = 5
/** Each line auto-disappears this long after it first shows. */
const AUTO_DISMISS_MS = 10000
/** Exit animation duration — line is removed from the DOM after this. */
const EXIT_MS = 320
const DESKTOP_MIN_WIDTH = 1024

/**
 * Reading budget for text that has to scroll before it can be read in full.
 *
 * A line is auto-dismissed after `AUTO_DISMISS_MS`, but long text is revealed by a marquee that takes
 * a full cycle to complete one pass. When the marquee is slower than the dismiss timer the tail of the
 * sentence is destroyed before it ever scrolls into view — the reason an incoming attack read as
 * "X is marching toward your…" and never finished. Lines that actually overflow get a full marquee
 * cycle plus a beat to read the revealed tail.
 */
const MARQUEE_CYCLE_MS = 30000
/** Grace period after the marquee completes, so the tail is readable and not just briefly flashed. */
const MARQUEE_READ_GRACE_MS = 2000
/** Longest a non-war line may live, however long its text is. */
const MAX_AUTO_DISMISS_MS = MARQUEE_CYCLE_MS + MARQUEE_READ_GRACE_MS
/** How long a homecoming line stays after the army actually lands, so the arrival is seen. */
const HOMECOMING_LINGER_MS = 15000

/**
 * How long a war line lives when nothing else retires it.
 *
 * War alerts used to have NO timer at all, on the theory that a raid warning must never expire while
 * the player looks away. In practice only the client-owned march alerts are ever cleared by something
 * else (GlobalAttackAlert removes them when the march resolves); every server-backed war line — siege
 * results, prisoner and ransom outcomes — had nothing to remove it and stayed on screen for the rest
 * of the session. They now live long enough to be impossible to miss, and no longer.
 */
const WAR_AUTO_DISMISS_MS = 45000
/** A live march keeps its countdown until impact, then lingers this long so the arrival is seen. */
const WAR_ARRIVAL_LINGER_MS = 30000
/** Ceiling for any war line: a march an hour out must not pin a banner to the screen for an hour. */
const WAR_MAX_LIFESPAN_MS = 10 * 60 * 1000

/**
 * `war` notifications (incoming attacks, sieges, prisoner/ransom outcomes) are the most time-critical
 * events in the game: acting late costs the player real assets. They are escalated out of the routine
 * ticker — tinted background, own stack above it, and a much longer life than a routine line.
 */
function isWar(n: Notification): boolean {
  return (n.type || 'system') === 'war'
}

/**
 * Arrival time for an incoming-march alert, in epoch ms.
 *
 * Prefers the live roster entry (`mvmRoster.byAttackId`), which the server keeps current as a march is
 * re-timed, and falls back to the `arrivesAt` captured on the notification when the attack fired.
 * Returns undefined for war notifications that are not a march — ransoms, prisoner outcomes and the
 * like describe something that already happened and have nothing to count down to.
 */
function arrivalMsFor(
  n: Notification,
  rosterByAttackId: Record<string, { arrivesAt?: string; returnArrivesAt?: string } | undefined>,
): number | undefined {
  const meta = n.metadata as Record<string, unknown> | null | undefined
  const kind = meta?.kind
  if (kind !== 'mvm-incoming' && kind !== 'mvm-returning') return undefined
  const attackId = typeof meta?.attackId === 'string' ? meta.attackId : undefined
  const entry = attackId ? rosterByAttackId[attackId] : undefined
  // A homecoming counts down to `returnArrivesAt`; an incoming march to `arrivesAt`.
  const live = kind === 'mvm-returning' ? entry?.returnArrivesAt : entry?.arrivesAt
  const raw = live ?? (typeof meta?.arrivesAt === 'string' ? meta.arrivesAt : undefined)
  if (!raw) return undefined
  const ms = new Date(raw).getTime()
  return Number.isFinite(ms) ? ms : undefined
}

/** True when the alert is a friendly march home rather than an enemy closing in. */
function isReturningMarch(n: Notification): boolean {
  return (n.metadata as Record<string, unknown> | null | undefined)?.kind === 'mvm-returning'
}

/**
 * Client-owned march alerts (incoming or homecoming), which GlobalAttackAlert both creates and
 * removes as the march resolves. Distinguishes them from server-backed notifications, whose absence
 * from the store means nothing about whether they are still relevant.
 */
function isMarchAlert(n: Notification): boolean {
  const kind = (n.metadata as Record<string, unknown> | null | undefined)?.kind
  return kind === 'mvm-incoming' || kind === 'mvm-returning'
}

/** `m:ss` once under an hour, `h:mm:ss` above it. */
function formatCountdown(remainingMs: number): string {
  const total = Math.max(0, Math.floor(remainingMs / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`
}

function notifKey(n: Notification): string {
  return String(n.id ?? n._id ?? n.timestamp ?? '')
}
function notifTime(n: Notification): number {
  return n.timestamp ?? (n.createdAt ? new Date(n.createdAt).getTime() : Date.now())
}

/**
 * Text colour by notification importance / priority (via its `type`):
 * war = most urgent (warm red), alliance / diplomacy = important (gold / blue),
 * reports / messages = routine (violet / green), system = low (neutral parchment).
 */
const NOTIF_TYPE_COLOR: Record<string, string> = {
  war: '#ff8663',
  alliance: '#ffd066',
  diplomacy: '#7fc4ff',
  reports: '#d7b8ff',
  messages: '#a7e0ad',
  system: '#e6dabb',
}
function notifColor(n: Notification): string {
  return NOTIF_TYPE_COLOR[n.type || 'system'] || '#f3ead0'
}

/** Injected once — enter/exit + marquee keyframes and hover/close styling for the ticker lines. */
const TickerStyles: React.FC = () => (
  <style>{`
    /* Same ribbon treatment as the land page's daily-request banner (DailyRequestBanner CONFIG):
       a soft black wash at 0.62 peak opacity that melts into the scene at its ends, Crimson Pro at
       the banner's own sizes, and the same double text shadow. The banner is centred here, so the
       wash fades on BOTH sides instead of only towards the map. No frame, no plate — it sits on
       the landscape exactly the way the daily request line does. */
    .gnt-toast {
      position: relative; display: flex; align-items: center; justify-content: center; gap: 8px;
      max-width: 100%; padding: 5px 26px; cursor: pointer;
      text-align: center;
      font-size: 11px; font-weight: 400; line-height: 1.35;
      font-family: var(--font-ui, 'Crimson Pro', Georgia, serif);
      color: #f6efdc;
      text-shadow: 0 1px 3px rgba(0,0,0,0.8), 0 0 10px rgba(0,0,0,0.45);
      border: none; border-radius: 0; box-shadow: none;
      background: linear-gradient(to right,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.508) 18%,
        rgba(0, 0, 0, 0.62) 50%,
        rgba(0, 0, 0, 0.508) 82%,
        rgba(0, 0, 0, 0) 100%);
      transition: background 0.18s ease;
      animation: gnt-in 0.28s ease-out both;
    }
    /* Matches the daily banner's desktop step (1024px / 16px). */
    @media (min-width: 1024px) { .gnt-toast { font-size: 16px; } }
    /* The type colour dresses nothing but the war/home accents now — the sentence itself stays the
       banner's parchment tone so every line reads the same way the daily request does. */
    .gnt-toast .gnt-line, .gnt-toast .gnt-war-body { color: #f6efdc; }
    .gnt-toast.gnt-exiting {
      animation: gnt-out 0.30s ease-in both;
      pointer-events: none;
    }
    .gnt-toast:hover {
      background: linear-gradient(to right,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.60) 18%,
        rgba(0, 0, 0, 0.74) 50%,
        rgba(0, 0, 0, 0.60) 82%,
        rgba(0, 0, 0, 0) 100%);
    }
    /* Drops in from the top edge it is now anchored to (was a slide in from the left corner). */
    @keyframes gnt-in { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes gnt-out { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-12px); } }
    .gnt-line {
      flex: 1 1 auto; min-width: 0; overflow: hidden; white-space: nowrap;
      -webkit-mask-image: linear-gradient(to right, transparent 0%, #000 9%, #000 91%, transparent 100%);
      mask-image: linear-gradient(to right, transparent 0%, #000 9%, #000 91%, transparent 100%);
    }
    .gnt-line-inner { display: inline-block; will-change: transform; }
    .gnt-line-marquee { animation: gnt-marquee 30s ease-in-out infinite; }
    /* Starts scrolling almost immediately and holds the end longer: the previous curve idled for the
       first 18% (5.4s) and reached the tail at 48% (14.4s), so a line dismissed at 10s was destroyed
       mid-sentence. */
    @keyframes gnt-marquee {
      0%, 6% { transform: translateX(0); }
      40%, 72% { transform: translateX(var(--gnt-shift, 0px)); }
      94%, 100% { transform: translateX(0); }
    }
    /* War alerts: legible against bright, busy terrain without needing hover. The routine ticker is
       transparent 13px/400 text over the landscape, which is unreadable for the one message class the
       player must act on immediately. */
    /* War keeps the daily-banner ribbon shape and font — only the wash is tinted and a touch
       heavier, because this is the one line the player must act on. */
    .gnt-toast-war {
      padding: 7px 24px;
      font-weight: 500;
      background: linear-gradient(to right,
        rgba(52, 12, 6, 0) 0%,
        rgba(52, 12, 6, 0.60) 18%,
        rgba(52, 12, 6, 0.74) 50%,
        rgba(52, 12, 6, 0.60) 82%,
        rgba(52, 12, 6, 0) 100%);
      animation: gnt-in 0.28s ease-out both, gnt-war-pulse 2.4s ease-in-out 0.28s 3;
    }
    .gnt-toast-war:hover {
      background: linear-gradient(to right,
        rgba(64, 15, 7, 0) 0%,
        rgba(64, 15, 7, 0.70) 18%,
        rgba(64, 15, 7, 0.86) 50%,
        rgba(64, 15, 7, 0.70) 82%,
        rgba(64, 15, 7, 0) 100%);
    }
    .gnt-toast-war .gnt-line, .gnt-toast-war .gnt-war-body { color: #ffeae2; }
    /* Three pulses, then settles — draws the eye on arrival without becoming permanent motion. */
    /* Brightness, not a box-shadow ring: the banner has no frame to ring any more, and this is the
       same pulse idiom the daily-request ribbon uses for a claimable reward. */
    @keyframes gnt-war-pulse {
      0%, 100% { filter: brightness(1); }
      50%      { filter: brightness(1.28); }
    }
    .gnt-war-label {
      flex: 0 0 auto; align-self: flex-start;
      font-size: 9px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase;
      color: #ffb9a3; opacity: 0.95; padding-top: 3px;
    }
    /* Message + countdown stack vertically inside the alert, so the ETA sits under the sentence
       rather than competing with it for the same line. */
    .gnt-war-body { flex: 1 1 auto; min-width: 0; }
    /* Time to impact — the one number the player acts on, so it gets its own line and tabular digits
       that do not reflow as the seconds tick down. */
    .gnt-war-eta {
      display: block; margin-top: 3px;
      font-size: 12px; font-weight: 500; letter-spacing: 0.02em;
      color: #ffc9b5;
      font-variant-numeric: tabular-nums;
    }
    .gnt-war-eta b {
      font-weight: 700; font-size: 13px; color: #fff0e8;
    }
    /* Landed: the march is over and the number stops being a countdown. */
    .gnt-war-eta--now b { color: #ffd9cc; }
    /* A homecoming is good news, not a threat: same readable plate, green accent, and none of the
       arrival pulse that marks an enemy closing in. */
    .gnt-toast-home {
      background: linear-gradient(to right,
        rgba(10, 34, 18, 0) 0%,
        rgba(10, 34, 18, 0.60) 18%,
        rgba(10, 34, 18, 0.74) 50%,
        rgba(10, 34, 18, 0.60) 82%,
        rgba(10, 34, 18, 0) 100%);
      animation: gnt-in 0.28s ease-out both;
    }
    .gnt-toast-home:hover {
      background: linear-gradient(to right,
        rgba(14, 44, 24, 0) 0%,
        rgba(14, 44, 24, 0.70) 18%,
        rgba(14, 44, 24, 0.86) 50%,
        rgba(14, 44, 24, 0.70) 82%,
        rgba(14, 44, 24, 0) 100%);
    }
    .gnt-toast-home .gnt-line, .gnt-toast-home .gnt-war-body { color: #e6f7e9; }
    .gnt-toast-home .gnt-war-label { color: #a9dfb4; }
    .gnt-toast-home .gnt-war-eta { color: #b6e6c0; }
    .gnt-toast-home .gnt-war-eta b { color: #ecfdf0; }
    /* War lines wrap instead of scrolling — the full sentence is visible at once, no waiting. */
    .gnt-toast-war .gnt-line {
      white-space: normal; overflow: visible;
      -webkit-mask-image: none; mask-image: none;
    }
    .gnt-toast-war .gnt-line-inner { display: block; animation: none !important; transform: none !important; }
    /* War lines outlive routine ones by a wide margin, so their dismiss control is always visible
       rather than waiting for a hover. */
    .gnt-toast-war .gnt-close { display: flex; opacity: 0.65; }
    .gnt-toast-war .gnt-close:hover { opacity: 1; }
    /* Pulled out of the flow and pinned to the right edge, so showing it on hover cannot nudge the
       centred sentence sideways. */
    .gnt-close {
      position: absolute; right: 4px; top: 50%; transform: translateY(-50%);
      flex: 0 0 auto; width: 22px; height: 22px; display: none;
      align-items: center; justify-content: center; border-radius: 50%;
      background: rgba(0,0,0,0.5); color: #f3ead0; font-size: 18px; line-height: 1;
      border: none; cursor: pointer; padding: 0;
    }
    .gnt-toast:hover .gnt-close { display: flex; }
    .gnt-close:hover { background: rgba(120, 30, 20, 0.7); }
    @media (prefers-reduced-motion: reduce) {
      .gnt-toast, .gnt-toast.gnt-exiting, .gnt-toast-war { animation: none; }
      .gnt-line-marquee { animation: none; }
    }
  `}</style>
)

/**
 * A single ticker line; only marquees (30s cycle) when the text actually overflows its width.
 *
 * Reports overflow via `onOverflowChange` so the host can extend that line's auto-dismiss to cover a
 * full marquee pass — otherwise scrolling text is removed before it finishes revealing itself.
 */
const TickerLine: React.FC<{ text: string; onOverflowChange?: (overflows: boolean) => void }> = ({
  text,
  onOverflowChange,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLSpanElement>(null)
  const [shift, setShift] = useState(0)
  const overflowCbRef = useRef(onOverflowChange)
  overflowCbRef.current = onOverflowChange

  useLayoutEffect(() => {
    const measure = () => {
      const w = wrapRef.current
      const i = innerRef.current
      if (!w || !i) return
      const overflow = i.scrollWidth - w.clientWidth
      const next = overflow > 4 ? overflow : 0
      setShift((prev) => {
        if (prev !== next) overflowCbRef.current?.(next > 0)
        return next
      })
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    if (wrapRef.current) ro.observe(wrapRef.current)
    if (innerRef.current) ro.observe(innerRef.current)
    return () => ro.disconnect()
  }, [text])

  return (
    <div ref={wrapRef} className="gnt-line">
      <span
        ref={innerRef}
        className={shift > 0 ? 'gnt-line-inner gnt-line-marquee' : 'gnt-line-inner'}
        style={shift > 0 ? ({ ['--gnt-shift' as string]: `-${shift}px` } as React.CSSProperties) : undefined}
      >
        {text}
      </span>
    </div>
  )
}

export const GlobalNotificationToastHost: React.FC = () => {
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const { address } = useWeb3Context()
  const notifications = useSelector((s: RootState) => s.notifications.notifications)
  const rosterByAttackId = useSelector((s: RootState) => s.mvmRoster?.byAttackId ?? {})
  // Read inside the arrival effect without making it a dependency — the roster updates far more often
  // than the timers need to be recomputed.
  const rosterRef = useRef(rosterByAttackId)
  rosterRef.current = rosterByAttackId

  const [items, setItems] = useState<Notification[]>([]) // newest first
  const [exitingKeys, setExitingKeys] = useState<Set<string>>(new Set())
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth >= DESKTOP_MIN_WIDTH : true,
  )

  const seenRef = useRef<Set<string>>(new Set())
  const mountTimeRef = useRef<number>(Date.now())
  const timersRef = useRef<Map<string, number>>(new Map())
  /** When each pending timer is due, so a reschedule can extend a line's life but never cut it short. */
  const deadlinesRef = useRef<Map<string, number>>(new Map())

  const hidden = isOnBattlePath(pathname) || pathname === '/start' || !address

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= DESKTOP_MIN_WIDTH)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  /**
   * Drives the incoming-march countdowns. Runs only while a march alert is actually on screen, so the
   * routine ticker never re-renders on a timer it has no use for.
   */
  const hasCountdown = items.some((n) => arrivalMsFor(n, rosterByAttackId) !== undefined)
  const [, setNowTick] = useState(0)
  useEffect(() => {
    if (!hasCountdown || hidden) return
    const id = window.setInterval(() => setNowTick((v) => v + 1), 1000)
    return () => window.clearInterval(id)
  }, [hasCountdown, hidden])

  // Remove a line with its exit animation (used by the auto-dismiss timer, close button, and cap).
  const dismiss = useCallback((key: string) => {
    const timer = timersRef.current.get(key)
    if (timer) {
      window.clearTimeout(timer)
      timersRef.current.delete(key)
    }
    deadlinesRef.current.delete(key)
    setExitingKeys((prev) => {
      if (prev.has(key)) return prev
      const next = new Set(prev)
      next.add(key)
      return next
    })
    window.setTimeout(() => {
      setItems((prev) => prev.filter((it) => notifKey(it) !== key))
      setExitingKeys((prev) => {
        if (!prev.has(key)) return prev
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }, EXIT_MS)
  }, [])

  /**
   * Arm (or push out) a line's auto-dismiss. Only ever moves the deadline later: the marquee
   * extension and the war lifespans both run through here, so whichever asks for the longest life
   * wins and nothing can cut a line short by rescheduling it.
   */
  const scheduleDismiss = useCallback(
    (key: string, dueAt: number) => {
      const current = deadlinesRef.current.get(key)
      if (current !== undefined && current >= dueAt) return
      const existing = timersRef.current.get(key)
      if (existing !== undefined) window.clearTimeout(existing)
      deadlinesRef.current.set(key, dueAt)
      timersRef.current.set(
        key,
        window.setTimeout(
          () => {
            timersRef.current.delete(key)
            deadlinesRef.current.delete(key)
            dismiss(key)
          },
          Math.max(0, dueAt - Date.now()),
        ),
      )
    },
    [dismiss],
  )

  // Only surface NEW notifications (arrived after mount). Everything already in the store — or that
  // arrives while on a hidden/battle page — is marked "seen" so it never pops up later.
  useEffect(() => {
    const fresh: Notification[] = []
    for (const n of notifications || []) {
      const key = notifKey(n)
      if (seenRef.current.has(key)) continue
      seenRef.current.add(key)
      if (!hidden && notifTime(n) >= mountTimeRef.current) fresh.push(n)
    }
    if (!fresh.length) return
    setItems((prev) => {
      const have = new Set(prev.map(notifKey))
      const add = fresh.filter((f) => !have.has(notifKey(f)))
      return [...add, ...prev]
    })
    for (const f of fresh) {
      const key = notifKey(f)
      if (timersRef.current.has(key)) continue
      /**
       * Every line gets a deadline now, war included. A march alert keeps its countdown until
       * impact (plus a beat to see the arrival) and is usually retired earlier by GlobalAttackAlert
       * when the march resolves; a war line with nothing to count down to — a siege result, a
       * ransom outcome — gets the flat war lifespan. Both are capped, so a missed "march resolved"
       * event can no longer strand a banner on screen for the rest of the session.
       */
      let lifespan = AUTO_DISMISS_MS
      if (isWar(f)) {
        const arrival = arrivalMsFor(f, rosterRef.current)
        const linger = isReturningMarch(f) ? HOMECOMING_LINGER_MS : WAR_ARRIVAL_LINGER_MS
        const untilEvent = arrival === undefined ? 0 : Math.max(0, arrival - Date.now())
        lifespan = Math.min(
          WAR_MAX_LIFESPAN_MS,
          Math.max(WAR_AUTO_DISMISS_MS, arrival === undefined ? 0 : untilEvent + linger),
        )
      }
      scheduleDismiss(key, Date.now() + lifespan)
    }
  }, [notifications, hidden, scheduleDismiss])

  /**
   * Extend a line's life once it reports that its text overflows: the marquee needs a full cycle to
   * reveal the tail, which outlasts the default 10s timer. `scheduleDismiss` only ever pushes a
   * deadline out, so this can never shorten a war line that was already given longer.
   */
  const handleOverflow = useCallback(
    (key: string, overflows: boolean) => {
      if (!overflows) return
      if (!timersRef.current.has(key)) return
      scheduleDismiss(key, Date.now() + MAX_AUTO_DISMISS_MS)
    },
    [scheduleDismiss],
  )

  /**
   * Retire a visible line when its notification leaves the store.
   *
   * A war line's own timer runs to the march's arrival, so without this a battle that resolves
   * early would leave an "Arriving now" line up for minutes: GlobalAttackAlert removes the
   * notification when the march is cancelled or the battle ends, and only this effect turns that
   * into a visible dismissal. The timer is the backstop for when that event never arrives.
   */
  useEffect(() => {
    const live = new Set((notifications || []).map(notifKey))
    for (const it of items) {
      const key = notifKey(it)
      // Only the client-owned march alerts are retired this way. A server-backed notification can be
      // absent simply because the last fetch was filtered by type, which must not dismiss its line.
      if (!isMarchAlert(it)) continue
      if (!live.has(key) && !exitingKeys.has(key)) {
        // Forget it too, so a later attack reusing this id (same attackId, new march) can show again.
        seenRef.current.delete(key)
        dismiss(key)
      }
    }
  }, [notifications, items, exitingKeys, dismiss])

  // Cap: keep only the newest 5 visible; the overflow (oldest) slides out. War alerts are capped on
  // their own stack so a burst of routine notifications can never push an unacknowledged attack out.
  useEffect(() => {
    const active = items.filter((it) => !exitingKeys.has(notifKey(it)))
    const war = active.filter(isWar)
    const routine = active.filter((it) => !isWar(it))
    if (war.length > MAX_TOASTS) {
      for (const it of war.slice(MAX_TOASTS)) dismiss(notifKey(it))
    }
    if (routine.length > MAX_TOASTS) {
      for (const it of routine.slice(MAX_TOASTS)) dismiss(notifKey(it))
    }
  }, [items, exitingKeys, dismiss])

  useEffect(() => {
    const timers = timersRef.current
    const deadlines = deadlinesRef.current
    return () => {
      for (const id of timers.values()) window.clearTimeout(id)
      timers.clear()
      deadlines.clear()
    }
  }, [])

  /**
   * Height of the MvM attack banner, which is fixed to the very top at z-index 9999 whenever a march
   * is inbound (`data-eclipsera-top-banner` on GlobalAttackAlert's stack). The ticker is anchored to
   * the same strip, so without this the incoming-attack line renders *underneath* the banner and the
   * player sees nothing at the one moment the alert matters. Measured on a slow poll only while lines
   * are actually on screen — the banner mounts and resizes on its own schedule, and this is far
   * cheaper than observing the whole document for it.
   */
  const [topBannerPx, setTopBannerPx] = useState(0)
  const hasVisibleItems = items.length > 0
  useEffect(() => {
    if (!hasVisibleItems || hidden) {
      setTopBannerPx(0)
      return
    }
    const measure = () => {
      const el = document.querySelector<HTMLElement>('[data-eclipsera-top-banner]')
      if (!el) {
        setTopBannerPx(0)
        return
      }
      const rect = el.getBoundingClientRect()
      // Only the top-anchored layout of that banner competes for this strip; its other layout is
      // pinned to the middle of the screen and must not push the ticker anywhere.
      const overlapsTopStrip = rect.height > 0 && rect.top <= 8
      setTopBannerPx(overlapsTopStrip ? Math.max(0, Math.round(rect.bottom)) : 0)
    }
    measure()
    const id = window.setInterval(measure, 500)
    return () => window.clearInterval(id)
  }, [hasVisibleItems, hidden])

  if (hidden) return null

  /* Tapping a toast marks the mail read and dismisses the toast. There is no
   * mail modal to open anymore — the message stays in the Global tab's mail
   * list, which is where it is read in full. */
  const handleOpen = (n: Notification) => {
    const id = n.id ?? n._id
    if (address && id) void dispatch(markNotificationRead(address, String(id)))
    dismiss(notifKey(n))
  }

  const isLand = pathname === '/'
  /**
   * Top centre, high on the screen: the land page's top menu bar is `w-fit` against the left edge
   * and the install plate hugs the right, so the middle of the top strip is free and the banner
   * can sit close to it. Safe-area is still added so notched phones do not tuck the first line
   * under the status bar.
   */
  const topInset = isDesktop ? (isLand ? '1.75rem' : '1.25rem') : isLand ? '1.25rem' : '0.85rem'
  // `topBannerPx` is the attack banner's bottom edge in viewport px — 0 when it is not up there.
  const top = topBannerPx > 0
    ? `${topBannerPx + 8}px`
    : `calc(max(4px, env(safe-area-inset-top)) + ${topInset})`

  const warItems = items.filter(isWar)
  const routineItems = items.filter((n) => !isWar(n))

  const renderToast = (n: Notification) => {
    const key = notifKey(n)
    const { title, message } = getNotificationDisplayText(n, t)
    // Show the full detail (message), prefixed with the title when they differ, so the user sees
    // exactly what happened. Long text marquees. Falls back to whichever part exists.
    const titleT = (title || '').trim()
    const messageT = (message || '').trim()
    let text: string
    if (titleT && messageT && messageT !== titleT) {
      text = messageT.startsWith(titleT) ? messageT : `${titleT} — ${messageT}`
    } else {
      text = messageT || titleT
    }
    if (!text) return null
    const exiting = exitingKeys.has(key)
    const war = isWar(n)

    // Live time-to-impact for an incoming march. Absent for war notifications that report a past
    // event, which keep the plain single-line treatment.
    const arrivalMs = arrivalMsFor(n, rosterByAttackId)
    const remainingMs = arrivalMs === undefined ? undefined : arrivalMs - Date.now()
    const homeward = isReturningMarch(n)
    const eta =
      remainingMs === undefined ? null : remainingMs > 0 ? (
        <span className="gnt-war-eta">
          {homeward
            ? t('notifications.warHomecomingIn', { defaultValue: 'Homecoming in' })
            : t('notifications.warArrivesIn', { defaultValue: 'Arrives in' })}{' '}
          <b>{formatCountdown(remainingMs)}</b>
        </span>
      ) : (
        <span className="gnt-war-eta gnt-war-eta--now">
          <b>
            {homeward
              ? t('notifications.warArrivedHome', { defaultValue: 'Home' })
              : t('notifications.warArrivedNow', { defaultValue: 'Arriving now' })}
          </b>
        </span>
      )
    return (
      <div
        key={key}
        className={`gnt-toast group pointer-events-auto${war ? ' gnt-toast-war' : ''}${
          homeward ? ' gnt-toast-home' : ''
        }${exiting ? ' gnt-exiting' : ''}`}
        style={{ color: homeward ? '#a7e0ad' : notifColor(n) }}
        onClick={() => handleOpen(n)}
        role="button"
        tabIndex={0}
        title={text}
      >
        {war && (
          <span className="gnt-war-label" aria-hidden>
            {homeward
              ? t('notifications.homeBadge', { defaultValue: 'HOME' })
              : t('notifications.warBadge', { defaultValue: 'WAR' })}
          </span>
        )}
        {eta ? (
          <div className="gnt-war-body">
            <TickerLine text={text} />
            {eta}
          </div>
        ) : (
          <TickerLine text={text} onOverflowChange={war ? undefined : (o) => handleOverflow(key, o)} />
        )}
        <button
          type="button"
          className="gnt-close"
          aria-label={t('common.close', { defaultValue: 'Close' }) as string}
          onClick={(e) => {
            e.stopPropagation()
            dismiss(key)
          }}
        >
          ×
        </button>
      </div>
    )
  }

  return (
    <>
      <TickerStyles />

      {/* Both stacks share one top-centre column so they can never overlap: war alerts on top of
          the routine ticker, each stack newest-first (`items` is newest first, so plain column
          order puts the freshest banner closest to the top edge). */}
      <div
        style={{
          position: 'fixed',
          top,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 200,
          maxWidth: 'calc(100vw - 1.5rem)',
          pointerEvents: 'none',
        }}
        className="flex flex-col items-center gap-2"
      >
        {/* War alerts: own stack, announced assertively for screen readers. */}
        {warItems.length > 0 && (
          <div
            style={{ width: isDesktop ? '30rem' : '19rem', maxWidth: '100%' }}
            className="flex flex-col gap-2"
            role="alert"
            aria-live="assertive"
          >
            {warItems.map(renderToast)}
          </div>
        )}

        <div
          style={{ width: isDesktop ? '26rem' : '17rem', maxWidth: '100%' }}
          className="flex flex-col gap-1.5"
          aria-live="polite"
        >
          {routineItems.map(renderToast)}
        </div>
      </div>
    </>
  )
}

export default GlobalNotificationToastHost
